import {RuleType} from "./RuleType";
import {ExcelData, PitchLimit, PitchResult, SavedData, SimulationData} from "./simulationTypes";

export const randomStr= (num:number): string => {
  const S = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  return Array.from(crypto.getRandomValues(new Uint32Array(num)))
    .map((n) => S[n % S.length])
    .join("");
}

export const pitchLimits = (ruleType: RuleType): PitchLimit => {
  if(ruleType === RuleType.young) return {oneDay: 80, twoDays: 120, threeGames: 40};
  if(ruleType === RuleType.junior) return {oneDay: 65, twoDays: 100, threeGames: 35};
  if(ruleType === RuleType.littleSenior) return {oneDay: 90, twoDays: 130, threeGames: 45};
  return {oneDay: 0, twoDays: 0, threeGames: 0}
}

export const createBlankData = (idStr: string, ruleType: RuleType):SimulationData => {
  const limits = pitchLimits(ruleType);
  return {
    id: idStr,
    name: '',
    team: '',
    number: '',
    today: {pitch_1:0, pitch_2:0, limit:limits.oneDay},
    oneDayAfter: {pitch_1:0, pitch_2:0, limit:limits.oneDay},
    twoDaysAfter: {pitch_1:0, pitch_2:0, limit:limits.oneDay},
    threeDaysAfter: {pitch_1:0, pitch_2:0, limit:limits.oneDay},
  }
}

export const changeDateStyle = (dateStr:string) => {
  const dateList = dateStr.split('/');
  return `${dateList[0]}-${('00'+dateList[1]).slice(-2)}-${('00'+dateList[2]).slice(-2)}`
}

export const createDateList = (dateStr:string) => {
  const baseDate = new Date(dateStr);
  const today = new Date(baseDate.setDate(baseDate.getDate()));
  const oneDayAfter = new Date(baseDate.setDate(baseDate.getDate()+1));
  const twoDaysAfter = new Date(baseDate.setDate(baseDate.getDate()+1));
  const threeDaysAfter = new Date(baseDate.setDate(baseDate.getDate()+1));
  return [today.toLocaleDateString(), oneDayAfter.toLocaleDateString(), twoDaysAfter.toLocaleDateString(), threeDaysAfter.toLocaleDateString()];
}

export const total = (result: PitchResult): number => {
  return result.pitch_1 + result.pitch_2;
}

export const clearPitch = (id:string, simulations: SimulationData[], rule:RuleType): SimulationData[] => {
  const limit = pitchLimits(rule);
  return simulations.map((simulation)=>{
    if(simulation.id === id) {
      return {
        id: simulation.id,
        name: simulation.name,
        team: simulation.team,
        number: simulation.number,
        today: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
        oneDayAfter: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
        twoDaysAfter: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
        threeDaysAfter: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
      }
    } else {
      return simulation;
    }
  });
}

export const clearAllPitch = (rule:RuleType, simulations:SimulationData[]): SimulationData[] => {
  const limit = pitchLimits(rule);
  return simulations.map((simulation)=>{
    return {
      id: simulation.id,
      name: simulation.name,
      team: simulation.team,
      number: simulation.number,
      today: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
      oneDayAfter: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
      twoDaysAfter: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
      threeDaysAfter: {pitch_1:0, pitch_2:0, limit:limit.oneDay},
    }
  });
}

// テーブルデータをエクセルデータの形に対応させる
export const createDataAllayToExcel = (date:string, simulations:SimulationData[]): ExcelData[] => {
  return simulations.map((simulation) => {
    return {
      date: date,
      id: simulation.id,
      name: simulation.name,
      team: simulation.team,
      number: simulation.number,
      todayPitch_1: simulation.today.pitch_1,
      todayPitch_2: simulation.today.pitch_2,
      todayLimit: simulation.today.limit,
      oneDayAfterPitch_1: simulation.oneDayAfter.pitch_1,
      oneDayAfterPitch_2: simulation.oneDayAfter.pitch_2,
      oneDayAfterLimit: simulation.oneDayAfter.limit,
      twoDaysAfterPitch_1: simulation.twoDaysAfter.pitch_1,
      twoDaysAfterPitch_2: simulation.twoDaysAfter.pitch_2,
      twoDaysAfterLimit: simulation.twoDaysAfter.limit,
      threeDaysAfterPitch_1: simulation.threeDaysAfter.pitch_1,
      threeDaysAfterPitch_2: simulation.threeDaysAfter.pitch_2,
      threeDaysAfterLimit: simulation.threeDaysAfter.limit,
    }
  })
}

export const changeToSimulationsStyle = (excelDataArray: ExcelData[]): SimulationData[] => {
  return excelDataArray.map((data)=>{
    return {
      id: data.id,
      name: data.name,
      team: data.team,
      number: data.number,
      today: {pitch_1:data.todayPitch_1, pitch_2:data.todayPitch_2, limit:data.todayLimit},
      oneDayAfter: {pitch_1:data.oneDayAfterPitch_1, pitch_2:data.oneDayAfterPitch_2, limit:data.oneDayAfterLimit},
      twoDaysAfter: {pitch_1:data.twoDaysAfterPitch_1, pitch_2:data.twoDaysAfterPitch_2, limit:data.twoDaysAfterLimit},
      threeDaysAfter: {pitch_1:data.threeDaysAfterPitch_1, pitch_2:data.threeDaysAfterPitch_2, limit:data.threeDaysAfterLimit},
    }
  });
}

// 対象日の残りの可能投球数を返す
export const restCheck = (pitchResult: PitchResult): number => {
  return pitchResult.limit - pitchResult.pitch_1 - pitchResult.pitch_2;
}

// その日の投球結果から翌日の可能投球数を返す
export const pitchAble_1 = (targetDayResult:PitchResult, ruleType:RuleType) => {
  const limit = pitchLimits(ruleType);
  if (targetDayResult.pitch_1 <= 0 && targetDayResult.pitch_2 <= 0) { //登板なし
    return limit.oneDay;
  } else if (targetDayResult.pitch_1 > limit.threeGames && targetDayResult.pitch_2 <= 0){ // 登板1 ＆ 3連投制限超え ケース1
    return (limit.twoDays - targetDayResult.pitch_1) < limit.twoDays - limit.oneDay ? limit.twoDays - limit.oneDay : limit.twoDays - targetDayResult.pitch_1;
  } else if (targetDayResult.pitch_1 <= 0 && targetDayResult.pitch_2 > limit.threeGames) { // 登板1 ＆ 3連投制限超え ケース2
    return (limit.twoDays - targetDayResult.pitch_2) < limit.twoDays - limit.oneDay ? limit.twoDays - limit.oneDay : limit.twoDays - targetDayResult.pitch_2;
  } else if (targetDayResult.pitch_1 <= limit.twoDays - limit.oneDay && targetDayResult.pitch_2 <= 0) { // 登板1 & （二日間制限-初日制限）以内 ケース1
    return limit.oneDay;
  } else if (targetDayResult.pitch_1 <= 0 && targetDayResult.pitch_2 <= limit.twoDays - limit.oneDay) { // 登板1 & （二日間制限-初日制限）以内 ケース2
    return limit.oneDay;
  } else if (targetDayResult.pitch_1 > limit.threeGames && targetDayResult.pitch_2 > 0) { // 登板2 1試合目が3連投制限超え & 2試合目登板 => 3連投禁止
    return 0;
  } else if (targetDayResult.pitch_1 > 0 && targetDayResult.pitch_1 < limit.threeGames && targetDayResult.pitch_2 > limit.threeGames) { // 登板2 1試合目が3連投制限未満 2試合目で3連投制限超え => 翌日投球禁止
    return 0;
  } else if (targetDayResult.pitch_1 === limit.threeGames && targetDayResult.pitch_2 > 0) { // 登板2 1試合目がちょうど3連投制限 2試合目で3連投制限超え(規定で3連投制限以内とみなす)  => 3連投OK
    return limit.threeGames;
  } else if (targetDayResult.pitch_1 > 0 && targetDayResult.pitch_1 <= limit.threeGames && targetDayResult.pitch_2 > 0 && targetDayResult.pitch_2 <= limit.threeGames) { // 登板2 1試合目が3連投制限以内 2試合目で3連投制限以内 => 3連投OK
    return limit.threeGames;
  } else { //見落とし
    return 9999;
  }
}

// シニア条件判定：当日単体→2日間で130球制限として可能投球数を返却
export const sPitchAble_1 = (targetDayResult:PitchResult): number => {
  const p1 = targetDayResult.pitch_1 <= 0 ? 0 : targetDayResult.pitch_1;
  const p2 = targetDayResult.pitch_2 <= 0 ? 0 : targetDayResult.pitch_2;
  const total = p1 + p2;
  if (p1 <= 0 && p2 <= 0) return 90; // 休養日
  if (total >= 90 ) return 40; // 当日の球数制限リミットに到達
  if ((130 - total) >= 90) return 90; // ワンポイント的投球で合計が40球未満
  return (130 - total); // 上記以外
}

// 前日の登板を加味した翌日の可能投球数の返却
export const pitchAble_2 = (beforeDayResult:PitchResult, targetDayResult:PitchResult, rule:RuleType) => {
  const limit = pitchLimits(rule);
  const before_p1 = beforeDayResult.pitch_1 <= 0 ? 0 : beforeDayResult.pitch_1;
  const before_p2 = beforeDayResult.pitch_2 <= 0 ? 0 : beforeDayResult.pitch_2;
  const p1 = targetDayResult.pitch_1 <= 0 ? 0 : targetDayResult.pitch_1;
  const p2 = targetDayResult.pitch_2 <= 0 ? 0 : targetDayResult.pitch_2;
  const beforeDayTotal = before_p1 + before_p2;
  const todayTotal = p1 + p2;
  const isBeforeDayPitched = (before_p1 !== 0 || before_p2 !== 0);
  const isTodayPitched = (p1 !== 0 || p2 !== 0);

  if (!isTodayPitched) return limit.oneDay; // 当日の登板なし
  if (!isBeforeDayPitched) return rule === RuleType.littleSenior ? sPitchAble_1(targetDayResult) : pitchAble_1(targetDayResult, rule); // 前日の登板なし

  // 以降は前日も当日も登板した対応になる該当する
  if(rule === RuleType.littleSenior){
    if (beforeDayTotal >= limit.oneDay) return 0; // 前日に90球以上投球
    if (todayTotal >= limit.oneDay) return 0; // 当日に90球以上投球
    if (beforeDayTotal + todayTotal > limit.oneDay) return 0; // 2日間で90球越え
    return targetDayResult.limit - todayTotal;
  } else { // ヤングルールとジュニアルール
    if (!!before_p1 && !!before_p2 && !!todayTotal) return 0; // 3連投した場合：先日に2連投
    if (!!beforeDayTotal && !!p1 && !!p2) return 0; // 3連投した場合:当日2連投
    //以降はpitchAble()による当日の登板制限がかかるため2試合登板状態の値が前提となる
    if (beforeDayTotal > limit.threeGames || todayTotal > limit.threeGames) return 0; // 三連投制限以上投球
    return limit.threeGames;
  }
}

// 全投球期間のデータから目的の範囲を選定する
const selectResult = (simulation: SimulationData, targetDayNum: number): PitchResult[] => {
  if(targetDayNum === 1) return [simulation.today, simulation.oneDayAfter];
  if (targetDayNum === 2) return [simulation.oneDayAfter, simulation.twoDaysAfter];
  return [simulation.twoDaysAfter, simulation.threeDaysAfter];
}

//チェック日における対象試合の投球可否を返す
export const checkPitchAble = (targetDay: PitchResult, beforeDay: PitchResult, targetGameNum: 1|2, rule:RuleType):boolean => {
  const limit = pitchLimits(rule);
  const isLimitZero = (targetDay.limit === 0);
  const isLimitOverAtGame1 = (targetDay.limit > 0 && targetGameNum === 2 && targetDay.limit - targetDay.pitch_1 <= 0);
  const isLimitOverAtGame2 = (targetDay.limit > 0 && targetGameNum === 1 && targetDay.limit - targetDay.pitch_2 <= 0);
  const canPitchOnlyOneGame = (targetDay.limit > 0 && (targetDay.limit < limit.oneDay) && (beforeDay.pitch_1 || beforeDay.pitch_2));
  const canPitchTwoGames = (targetDay.limit === limit.oneDay && (beforeDay.pitch_1 || beforeDay.pitch_2));

  //基本条件
  if (isLimitZero) return false;
  if (isLimitOverAtGame1) return false;
  if (isLimitOverAtGame2) return false;

  //ヤングとジュニアは試合数による連投制限がある（リトルシニアはダブルヘッダー登板等の制限なし）
  if (rule === RuleType.young || rule === RuleType.junior) {
    //前日の投球数の結果で分岐
    if (canPitchOnlyOneGame) {
      if (targetDay.pitch_1 > 0 && targetGameNum === 2) return false;
      if (targetDay.pitch_2 > 0 && targetGameNum === 1) return false;
    }
    if (canPitchTwoGames) {
      if (targetDay.pitch_1 > limit.threeGames && targetGameNum === 2) return false;
      //3連投特殊用にこの記述をしている(初日1登板、2日目2登板で3試合目が3連投制限を超えた場合に、2試合目がfalseになるエラー対応）
      if (targetDay.pitch_2 > limit.threeGames && targetGameNum === 1) return !!targetDay.pitch_1;
    }
  }
  return true;
}

export const isPitchAble = (simulation: SimulationData, targetDayNum: number, targetGameNum: 1|2, rule:RuleType): boolean => {
  const beforeDay = selectResult(simulation, targetDayNum)[0];
  const targetDay = selectResult(simulation, targetDayNum)[1];
  return checkPitchAble(targetDay, beforeDay, targetGameNum, rule);
}

export const saveLocalStorage = (rule: RuleType, dateStr:string, dateList:string[], simulations:SimulationData[]): void => {
  if(rule === RuleType.young) {
    localStorage.setItem('dateStr', dateStr);
    localStorage.setItem('dateList', JSON.stringify(dateList));
    localStorage.setItem('data', JSON.stringify(simulations));
  } else if (rule === RuleType.junior) {
    localStorage.setItem('dateStr_jr', dateStr);
    localStorage.setItem('dateList_jr', JSON.stringify(dateList));
    localStorage.setItem('data_jr', JSON.stringify(simulations));
  } else if (rule === RuleType.littleSenior) {
    localStorage.setItem('dateStr_ls', dateStr);
    localStorage.setItem('dateList_ls', JSON.stringify(dateList));
    localStorage.setItem('data_ls', JSON.stringify(simulations));
  }
}

// 適用ルール別でローカルストレージの保存データを取得する
export const loadLocalStorage = (rule:RuleType): SavedData => {
  if (rule === RuleType.young) {
    const initDateStr = localStorage.getItem('dateStr') ?? '';
    const initDateList = localStorage.getItem('dateList') ?? '';
    const initData = localStorage.getItem('data') ?? '';
    return {date:initDateStr, dateList: initDateList, simulations: initData}
  } else if (rule === RuleType.junior) {
    const initDateStr = localStorage.getItem('dateStr_jr') ?? '';
    const initDateList = localStorage.getItem('dateList_jr') ?? '';
    const initData = localStorage.getItem('data_jr') ?? '';
    return {date:initDateStr, dateList: initDateList, simulations: initData}
  } else if (rule === RuleType.littleSenior) {
    const initDateStr = localStorage.getItem('dateStr_ls') ?? '';
    const initDateList = localStorage.getItem('dateList_ls') ?? '';
    const initData = localStorage.getItem('data_ls') ?? '';
    return {date:initDateStr, dateList: initDateList, simulations: initData}
  }
  return {date:'', dateList: '', simulations: ''}
}
